// SignUp.js
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { AlertTriangle, CheckCircle } from 'lucide-react';
import { Button } from '../../components/js-components/button';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

function SignUp() {
  const [formData, setFormData] = useState({
    username: '',
    email: '',
    password: '',
    name: '',
  });
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  const validatePassword = (password) => {
    const regex = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)[A-Za-z\d@$!%*?&]{8,}$/;
    return regex.test(password);
  };

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.id]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    setLoading(true);

    // Validate name
    if (formData.name.trim().length < 2) {
      setError('Name must be at least 2 characters long.');
      setLoading(false);
      return;
    }

    // Validate password
    if (!validatePassword(formData.password)) {
      setError('Password must be at least 8 characters long, include an uppercase letter and a number.');
      setLoading(false);
      return;
    }

    try {
      await axios.post(`${API_BASE_URL}/auth/signup`, formData);
      setSuccess('Account created successfully! Redirecting to login...');
      setTimeout(() => navigate('/login'), 5000);
    } catch (err) {
      setError(err.response?.data?.message || 'Signup failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="signup-container flex justify-center items-center min-h-screen bg-gray-100">
      <div className="signup-card w-full max-w-md bg-white shadow-lg rounded-xl p-8">
        {error && (
          <div 
            className="error-banner flex items-center bg-red-100 border-l-4 border-red-500 text-red-700 p-4 mb-6 rounded-r-lg shadow-md" 
            role="alert"
          >
            <AlertTriangle className="mr-4 text-red-500" size={24} />
            <span className="font-semibold text-sm">{error}</span>
          </div>
        )}

        {success && (
          <div 
            className="success-banner flex items-center bg-green-100 border-l-4 border-green-500 text-green-700 p-4 mb-6 rounded-r-lg shadow-md" 
            role="alert"
          >
            <CheckCircle className="mr-4 text-green-500" size={24} />
            <span className="font-semibold text-sm">{success}</span>
          </div>
        )}

        <h2 className="text-2xl font-bold text-center mb-6 text-gray-800">Create Your Account</h2>
        
        <form onSubmit={handleSubmit}>
          <div className="mb-4">
            <label 
              htmlFor="name" 
              className="block text-gray-700 text-sm font-bold mb-2"
            >
              Full Name
            </label>
            <input
              type="text"
              id="name"
              value={formData.name}
              onChange={handleChange}
              placeholder="Enter your full name"
              required
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>

          <div className="mb-4">
            <label 
              htmlFor="username" 
              className="block text-gray-700 text-sm font-bold mb-2"
            >
              Username
            </label>
            <input
              type="text"
              id="username"
              value={formData.username}
              onChange={handleChange}
              placeholder="Choose a username"
              required
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>

          <div className="mb-4">
            <label 
              htmlFor="email" 
              className="block text-gray-700 text-sm font-bold mb-2"
            >
              Email
            </label>
            <input
              type="email"
              id="email"
              value={formData.email}
              onChange={handleChange}
              placeholder="Enter your email"
              required
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>

          <div className="mb-6">
            <label 
              htmlFor="password" 
              className="block text-gray-700 text-sm font-bold mb-2"
            >
              Password
            </label>
            <input
              type="password"
              id="password"
              value={formData.password}
              onChange={handleChange}
              placeholder="Create a password"
              required
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
            <p className="text-xs text-gray-500 mt-1">
              Password must be at least 8 characters, include uppercase, lowercase, and a number.
            </p>
          </div>

          <Button 
            type="submit" 
            disabled={loading} 
            className="w-full bg-red-500 text-white py-2 rounded-md hover:bg-red-600 transition duration-300 ease-in-out"
          >
            {loading ? "Creating Account..." : "Sign Up"}
          </Button>
        </form>

        <p className="text-center mt-4 text-sm text-gray-600">
          Already have an account? {" "}
          <a 
            href="/login" 
            className="text-blue-500 hover:text-blue-700 font-semibold"
          >
            Log in
          </a>
        </p>
      </div>
    </div>
  );
}

export default SignUp;